import { memo } from 'react';
import { Box, Typography, Alert, Button, Chip, CircularProgress } from '@mui/material';
import type { SubscribePreview } from '../hooks/useSubscribeFlow';

interface SubscribePreviewPanelProps {
  preview: SubscribePreview | null;
  loading: boolean;
  error: string | null;
  canPreview: boolean;
  onRunPreview: () => void;
}

const SubscribePreviewPanel = memo<SubscribePreviewPanelProps>(({
  preview,
  loading,
  error,
  canPreview,
  onRunPreview,
}) => {
  const deployments = preview ? preview.deployments_to_create : [];
  const portfolios = preview ? preview.portfolios_to_share : [];
  const deploymentCount = preview ? preview.deployment_count : 0;
  const portfolioCount = preview ? preview.portfolio_count : 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle2">Preview</Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={onRunPreview}
          disabled={!canPreview || loading}
          startIcon={loading ? <CircularProgress size={14} /> : undefined}
        >
          {loading ? 'Loading…' : 'Preview'}
        </Button>
        {preview && !loading && (
          <Typography variant="caption" color="text.secondary">
            {deploymentCount} deployment(s), {portfolioCount} portfolio share(s).
          </Typography>
        )}
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      {preview && (
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 1.5,
            bgcolor: 'background.default',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Deployments to create
            </Typography>
            {deployments.length === 0 ? (
              <Typography variant="body2" color="text.disabled">None — all components already deployed</Typography>
            ) : (
              deployments.map((d) => (
                <Box
                  key={`${d.product_id}-${d.region}`}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}
                >
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {d.product_name} · {d.region}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({d.service})
                  </Typography>
                  {d.is_reactivation && (
                    <Chip label="reactivation" size="small" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
                  )}
                </Box>
              ))
            )}
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Portfolios to share
            </Typography>
            {portfolios.length === 0 ? (
              <Typography variant="body2" color="text.disabled">None — already shared</Typography>
            ) : (
              portfolios.map((p) => (
                <Typography key={p.portfolio} variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                  {p.portfolio} → {p.regions.join(', ')}
                </Typography>
              ))
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
});

SubscribePreviewPanel.displayName = 'SubscribePreviewPanel';
export default SubscribePreviewPanel;
